import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
    Box, Button,
    Checkbox,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Select,
    Text,
    Tooltip,
    useToast
} from '@chakra-ui/react';
import CustomDeepLinkInput from 'components/genericForms/CustomDeepLinkInput';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useUpdateAdspressoAdCaroByAdIdMutation } from 'store/adspresso/adspressoAdApi';
import { useGetAdvertisableAppsQuery } from 'store/campaigns/adsetFormApi';
import {
    call_to_action_options
} from 'utils/callToActions';
import { removeEmptyValues } from 'utils/functions';
import { object, string } from "yup";
import BuildUrlParamsUpdate from '../BuildUrlParamsUpdate';
import UrlParametersUpdate from '../UrlParametersUpdate';

const mustStartWithHttpRegex = /^https?:\/\/.+$/;

let schema = object().shape({
    type: string()
        .required('Type is required'),
    message: string(),
    link: string()
        .url('Please add a valid URL')
        .when('type', {
            is: (type) => type !== 'CALL_NOW',
            then: () => string().required('Url is required'),
            otherwise: () => string().notRequired(),
    }),
    template_url_spec: object().shape({
        android: object().shape({
            url: string()
            .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://')
        }),
        ios: object().shape({
            url: string()
            .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://')
        }),
        web: object().shape({
            url: string()
            .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://')
        }),

    })
})


const CatalogueCaroAdUpdateForm = ({ adData, creative, adAccount, objective }) => {

    const toast = useToast();
    const { link, type, url_tags, _id, contextual_multi_ads, fileName, asset_feed_spec, caption, template_url_spec, adsAccount, applink_treatment, message } = creative || {}

    const destinationType = creative?.adsets[0]?.destination_type;
    const [isUrlParamModalOpen, setUrlParamModalOpen] = useState(false);
    const [isUrlModalOpen, setUrlModalOpen] = useState(false);
    const [updateAdspressoCarocreativeAds, { isLoading: isAdspressoCaroAdUpdating }] = useUpdateAdspressoAdCaroByAdIdMutation({});
    const { data: advertisableApps } = useGetAdvertisableAppsQuery({adsAccountId: adsAccount?.adsAccountId}, {skip: !adsAccount?.adsAccountId});

    const caroMediaFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            message: message || '',
            type: type || '',
            link: link || '',
            caption: caption || '',
            url_tags: url_tags || '',
            fileName: fileName || '',
            enroll_status: contextual_multi_ads?.enroll_status ? contextual_multi_ads?.enroll_status : 'OPT_OUT',
            app_product_page_id: asset_feed_spec?.app_product_page_id,
            applink_treatment: applink_treatment ? applink_treatment : 'web_only',
            template_url_spec: {
                web: {
                  url: template_url_spec?.web?.url || '',
                },
                config: {
                  app_id: template_url_spec?.config?.app_id || ''
                },
                android: {
                  url: template_url_spec?.android?.url || '',
                },
                ios: {
                  url: template_url_spec?.ios?.url || '',
                },
            },
            override_catalog: template_url_spec?.web?.url ? true : false,
        },
        validationSchema: schema,
        onSubmit: (values, { resetForm }) => {
            const { link, message, type, url_tags, fileName, enroll_status, caption, applink_treatment, template_url_spec } = values;

            const templateUrlSpec =
                applink_treatment === 'web_only'
                    ? {
                        web: {
                            url: template_url_spec?.web?.url || '',
                        },
                    }
                    : {
                        web: {
                            url: template_url_spec?.web?.url || '',
                        },
                        ios: {
                            url: template_url_spec?.ios?.url || '',
                        },
                        android: {
                            url: template_url_spec?.android?.url || '',
                        },
                        config: {
                            app_id: template_url_spec?.config?.app_id || '',
                        },
                    };

            const initialPayload = {
                fileName,
                contextual_multi_ads: {
                    enroll_status,
                },
                applink_treatment,
                asset_feed_spec: {
                    ad_formats: [
                        "CAROUSEL",
                        "COLLECTION"
                    ],
                    optimization_type: "FORMAT_AUTOMATION",
                },
                message,
                type,
                caption,
                template_url_spec: templateUrlSpec,
                // degrees_of_freedom_spec: {
                //     creative_features_spec: {
                //       standard_enhancements: { enroll_status: "OPT_OUT" },
                //     },
                // },
                ...(url_tags?.trim() && { url_tags }),
                ...(link && { link: link }),
            };

            const payload = removeEmptyValues(initialPayload);

            try {
                updateAdspressoCarocreativeAds({ _id, payload }).unwrap()
                    .then(response => {
                        toast({
                            position: "top-right",
                            title: "Ad updated",
                            status: "success",
                            duration: 4000,
                            isClosable: true,
                        });
                    })
                    .catch(error => {
                        const errorMessage = error?.data?.message || "An unexpected error occurred";
                        const formattedMessage = errorMessage.replace(/['"]/g, '');

                        toast({
                            position: "top-right",
                            title: "Error updating Ad",
                            description: formattedMessage,
                            status: "error",
                            duration: 4000,
                            isClosable: true,
                        });
                    });
            } catch (error) {
                // console.log(error);

            } finally {
                resetForm();
            }
        }
    });

    const handleUrlChange = (constructedUrl) => {
        caroMediaFormik.setFieldValue('link', constructedUrl);
    };

    const handleUrlParamChange = (constructedUrl) => {
        caroMediaFormik.setFieldValue('url_tags', constructedUrl);
    };


    return (
        <>
            <Flex py={2} direction={'column'} gap={2}>
                <Flex direction="column">
                    {/* {Boolean(adData?.useFileName) ? (
                        <Flex mb={4} direction="column">
                            <Flex alignItems="center" mb={2} gap={2}>
                                <Text fontSize={'14px'} fontWeight="bold">Ad name</Text>
                            </Flex>
                            <Input
                                placeholder="Ad name"
                                name="fileName"
                                size={'sm'}
                                borderRadius="md"
                                value={caroMediaFormik.values.fileName}
                                onChange={caroMediaFormik.handleChange}
                            />
                        </Flex>
                    ) : null} */}
                    {/* Multi-advertiser ads Section */}
                    <Flex direction="column" mb={4} borderRadius="md" gap={2}>
                        <Checkbox
                            onChange={(e) => {
                                caroMediaFormik.setFieldValue("enroll_status", e.target.checked ? 'OPT_IN' : 'OPT_OUT');
                            }}
                            colorScheme="brand"
                            isChecked={caroMediaFormik.values.enroll_status === 'OPT_IN'}
                        >
                            <Flex align={'center'}>
                                <Text fontWeight="bold" fontSize={'14px'}>Multi-advertiser ads &nbsp;</Text>
                                <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="12px" p="5px">
                                            <Text>Your ads can appear alongside other ads in the same ad unit to help people discover products and services from businesses that are personalized to them. Your ad creative may be resized or cropped to fit the ad unit.</Text>
                                        </Box>
                                    }
                                    fontSize="md"
                                >
                                    <InfoOutlineIcon pointerEvents="all" />
                                </Tooltip>
                            </Flex>
                        </Checkbox>
                    </Flex>

                    <Flex direction="column" gap={1}>
                        <Flex alignItems="center">
                            <Text fontWeight="bold">Destination</Text>
                        </Flex>
                        <Flex alignItems="center" gap={2}>
                            <Text fontWeight="bold" fontSize={'14px'}>Default destination</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="12px" p="5px">
                                        <Text>
                                            Send people to either your app or website URL.
                                        </Text>
                                    </Box>
                                }
                                fontSize="md">
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>
                        <Select
                            name="applink_treatment"
                            bg="#fff"
                            size="sm"
                            borderRadius="md"
                            onChange={caroMediaFormik.handleChange}
                            onBlur={caroMediaFormik.handleBlur}
                            value={caroMediaFormik.values.applink_treatment}
                        >
                            <option value="web_only">Website</option>
                            <option value="deeplink_with_web_fallback">App</option>
                        </Select>
                    </Flex>

                    {caroMediaFormik.values.applink_treatment === 'deeplink_with_web_fallback' ?
                        <>
                            <Flex direction={'column'} gap={1} mt={'10px'}>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize={'14px'}>Mobile app</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="12px" p="5px">
                                                <Text>Only select a mobile app if you are using a universal link (iOS or Android app link).</Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                <Select
                                    name={`template_url_spec.config.app_id`}
                                    bg="#fff"
                                    size="sm"
                                    borderRadius="md"
                                    onChange={caroMediaFormik.handleChange}
                                    onBlur={caroMediaFormik.handleBlur}
                                    value={caroMediaFormik?.values?.template_url_spec?.config?.app_id}
                                >
                                    <option value="">Choose app</option>
                                    {advertisableApps?.map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option.name}
                                        </option>
                                    ))}
                                </Select>
                            </Flex>

                            <Flex direction={'column'} gap={2} mt={'10px'}>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize="14px">iOS deep link</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="14px" p="5px">
                                                <Text>
                                                    Use a deep link to specify where in your app to send people. Entering an iOS deep link here will override any iOS deep links for items in the selected catalog.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                <CustomDeepLinkInput
                                    value={caroMediaFormik.values?.template_url_spec?.ios?.url}
                                    onChange={(url) => {
                                        caroMediaFormik.setFieldValue('template_url_spec.ios.url', url)
                                    }}
                                    error={
                                        caroMediaFormik.errors?.template_url_spec?.ios?.url
                                    }
                                    placeholder={'Enter the deep link URL'}
                                    maxW={'350px'}
                                />
                            </Flex>

                            <Flex direction={'column'} gap={2} mt={'10px'}>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize="14px">Android deep link</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="14px" p="5px">
                                                <Text>
                                                    Use a deep link to specify where in your app to send people. Entering an Android deep link here will override any Android deep links for items in the selected catalog.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                <CustomDeepLinkInput
                                    value={caroMediaFormik.values?.template_url_spec?.android?.url}
                                    onChange={(url) => caroMediaFormik.setFieldValue('template_url_spec.android.url', url)}
                                    error={
                                        caroMediaFormik.errors?.template_url_spec?.android?.url
                                    }
                                    placeholder={'Enter the deep link URL'}
                                     maxW={'350px'}
                                />
                            </Flex>
                        </>
                    : null}

                    <Flex direction="column">
                        <Flex alignItems="center" gap={2} my={'10px'}>
                            <Text fontWeight="bold" fontSize={'14px'}>Website URL</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="12px" p="5px">
                                        <Text>Enter the URL for the webpage you want people to visit</Text>
                                    </Box>
                                }
                                fontSize="md"
                            >
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>
                        <Flex direction={'column'} gap={1}>
                            <Input
                                type="url"
                                name="link"
                                borderRadius={'md'}
                                placeholder="Enter URL"
                                value={caroMediaFormik.values.link}
                                onChange={caroMediaFormik.handleChange}
                                size={'sm'}
                            />
                             <Flex mt={2}>
                                <Button onClick={() => {
                                    setUrlModalOpen(true)
                                }}
                                    variant="link"
                                    color='brand'>
                                    <Text fontSize={'12px'} textDecoration={'underline'}>{'Build a URL parameter'}</Text>
                                </Button>
                            </Flex>
                            {caroMediaFormik.touched.link && caroMediaFormik.errors.link ? (
                                <Text color="red.500" fontSize={'12px'}>{caroMediaFormik.errors.link}</Text>
                            ) : null}
                        </Flex>
                    </Flex>

                    <Flex direction="column" gap={2} my={'10px'}>
                        <Text fontWeight="bold" fontSize={'14px'}>Display link</Text>
                        <Input
                            name="caption"
                            placeholder="Enter the link that you want to show on your ad"
                            bg="#fff"
                            size='sm'
                            borderRadius="md"
                            value={caroMediaFormik.values.caption}
                            onChange={caroMediaFormik.handleChange}
                        />
                    </Flex>

                    <Flex direction="column" mb={2} borderRadius="md" gap={2} mt={'10px'}>
                        <Checkbox
                            onChange={(e) => {
                                caroMediaFormik.setFieldValue("override_catalog", e.target.checked);
                            }}
                            colorScheme="brand"
                            isChecked={caroMediaFormik.values.override_catalog}
                            alignItems="flex-start"
                        >
                            <Flex direction="column">
                                <Flex align="center" mt={'-3px'}>
                                    <Text fontWeight="bold" fontSize="14px">
                                        Override catalog website deep links&nbsp;
                                    </Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="12px" p="5px">
                                                <Text>
                                                    Link each item in your catalog to a specific page on your website.
                                                    You can add deep links in your catalog's data feed. Use a deep link
                                                    to specify where in your website to send people. Entering a deep link
                                                    to website here will override any website deep links for items in the
                                                    selected catalog.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                {/* Description text on the next line */}
                                <Text fontSize="11px">
                                    This allows you to override any website deep links for items in the selected catalog.
                                </Text>
                            </Flex>
                        </Checkbox>
                        {caroMediaFormik.values.override_catalog ?
                            <>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize="14px">Set catalog deep links</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="14px" p="5px">
                                                <Text>
                                                    Enter the URL for the website deep link you want people to visit. This URL will override the destination links in your catalog.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>

                                <CustomDeepLinkInput
                                    value={caroMediaFormik.values?.template_url_spec?.web?.url}
                                    onChange={(url) => caroMediaFormik.setFieldValue('template_url_spec.web.url', url)}
                                    error={
                                        caroMediaFormik.touched?.template_url_spec?.web?.url &&
                                        caroMediaFormik.errors?.template_url_spec?.web?.url
                                    }
                                    placeholder={'http://www.example.com/page'}
                                     maxW={'350px'}
                                />

                            </>
                            : null}
                    </Flex>

                    <Flex alignItems="center" mb={2} gap={2}>
                        <Text fontSize="14px" fontWeight="bold">Primary text</Text>
                        <Tooltip
                            borderRadius={5}
                            label={
                                <Box fontSize="12px" p="5px">
                                    <Text>
                                        The primary text for your ad appears in most placements, though the position
                                        varies. You can use @ to tag Facebook profiles or Pages. We recommend 125
                                        characters or less.
                                    </Text>
                                </Box>
                            }
                            fontSize="md"
                        >
                            <InfoOutlineIcon pointerEvents="all" />
                        </Tooltip>
                    </Flex>
                    <CustomDeepLinkInput
                        value={caroMediaFormik.values.message}
                        onChange={(message) => caroMediaFormik.setFieldValue('message', message)}
                        error={
                            caroMediaFormik?.touched?.message &&
                            caroMediaFormik?.errors?.message
                        }
                        placeholder={'Tell people what your ad is about..'}
                         maxW={'350px'}
                    />
                </Flex>

                <Box direction="column">
                    <Flex alignItems="center" mb={2} gap={2}>
                        <Text fontWeight="bold" fontSize={'14px'}>Call to action</Text>
                        <Tooltip
                            borderRadius={5}
                            label={
                                <Box fontSize="12px" p="5px">
                                    <Text>Show a button or link on your ad that represents the action you want people to take.</Text>
                                </Box>
                            }
                            fontSize="md"
                        >
                            <InfoOutlineIcon pointerEvents="all" />
                        </Tooltip>
                    </Flex>
                    <Select
                        name="type"
                        borderRadius={'md'}
                        value={caroMediaFormik.values.type}
                        onChange={caroMediaFormik.handleChange}
                        size={'sm'}
                    >

                        {call_to_action_options.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </Box>

                {destinationType === 'APP' ? (
                    <>
                        <Flex direction="column" gap={2}>
                            <Flex alignItems="center" gap={2}>
                                <Text fontWeight="bold" fontSize={'14px'}>Deferred deep link</Text>
                                <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="12px" p="5px">
                                            <Text fontWeight={'bold'}>Using a deferred deep link</Text><br></br>
                                            <Text mb={2}>A deferred deep link is a URL that sends people to your app in the respective app store. Once people install your app, the link will take them to a specific screen, such as a product page.</Text>
                                            <Text mb={2}>To use a deferred deep link, you need to first connect the Facebook SDK. You can use any of these link types:</Text>
                                            <Box>
                                                <Text>* Android app link</Text>
                                                <Text>* Custom URL scheme</Text>
                                                <Text>* Facebook app link</Text>
                                            </Box>
                                        </Box>
                                    }
                                    fontSize="md"
                                >
                                    <InfoOutlineIcon pointerEvents="all" />
                                </Tooltip>
                            </Flex>
                            <Input
                                type="url"
                                name="link"
                                borderRadius={'md'}
                                placeholder="Enter URL"
                                value={caroMediaFormik.values.link}
                                onChange={caroMediaFormik.handleChange}
                                size={'sm'}
                            />
                        </Flex>

                        <Flex direction="column" gap={2}>
                            <Flex alignItems="center" gap={2}>
                                <Text fontWeight="bold" fontSize="14px">Custom store listing</Text>
                                <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="12px" p="5px">
                                            <Text fontWeight="bold">Using a custom store listing</Text><br />
                                            <Text mb={2}>A custom store listing ID sends people to a landing page you've customized...</Text>
                                        </Box>
                                    }
                                    fontSize="md"
                                >
                                    <InfoOutlineIcon pointerEvents="all" />
                                </Tooltip>
                            </Flex>
                            <Input
                                placeholder="Enter custom store listing ID"
                                name="app_product_page_id"
                                size={'sm'}
                                borderRadius="md"
                                value={caroMediaFormik.values.app_product_page_id}
                                onChange={caroMediaFormik.handleChange}
                            />
                        </Flex>
                    </>
                ) : null}

                <Flex direction="column" gap={1}>
                    <Flex alignItems={'center'} gap={2}>
                        <Text fontWeight="bold" fontSize={'14px'}>
                            URL parameters{" "}
                            {/* <Text as="span" fontWeight="bold" color={'gray.600'} mr={1}>.</Text> */}
                            <Text as="span" fontWeight="bold" color={'gray.600'}>Optional</Text>
                        </Text>
                        <Tooltip
                            borderRadius={5}
                            label={
                                <Box fontSize="12px" p={'5px'}>
                                    <Text >
                                        Add parameters to the end of your website URL to track where your visitors are coming from.
                                    </Text>
                                </Box>
                            }
                            fontSize="md"
                        >
                            <InfoOutlineIcon pointerEvents="all" />
                        </Tooltip>
                    </Flex>
                    <Input
                        name="url_tags"
                        placeholder="key1=value1&key2=value2"
                        bg="#fff"
                        size='sm'
                        borderRadius="md"
                        value={caroMediaFormik.values.url_tags}
                        onChange={caroMediaFormik.handleChange}
                    />
                    <Flex mt={1}>
                        <Button onClick={() => {
                            setUrlParamModalOpen(true)
                        }}
                            variant="link"
                            color='brand'>
                            <Text fontSize={'12px'} >{'Build a URL parameter'}</Text>
                        </Button>
                    </Flex>
                    {caroMediaFormik.touched?.url_tags && caroMediaFormik.errors?.url_tags && (
                        <Text fontSize={"12px"} color="red.500">{caroMediaFormik.errors?.url_tags}</Text>
                    )}
                </Flex>


                <Flex justifyContent="flex-start" mt={'5px'}>
                    <Button
                        colorScheme="brand"
                        onClick={() => caroMediaFormik.handleSubmit()}
                        size={'sm'}
                        isLoading={isAdspressoCaroAdUpdating}
                        isDisabled={isAdspressoCaroAdUpdating}
                    >
                        Save
                    </Button>
                </Flex>
            </Flex>

            <Modal isOpen={isUrlModalOpen} onClose={() => setUrlModalOpen(false)} scrollBehavior={"inside"}
                size={"2xl"}
                isCentered={true}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader css={{ boxShadow: '0 0px 5px rgba(0, 0, 0, 0.1)' }}>Build a URL parameter</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <BuildUrlParamsUpdate link={caroMediaFormik?.values} onClose={() => setUrlModalOpen(false)} onUrlChange={handleUrlChange} />
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Modal isOpen={isUrlParamModalOpen} onClose={() => setUrlParamModalOpen(false)} scrollBehavior={"inside"}
                size={"2xl"}
                isCentered={true}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader css={{ boxShadow: '0 0px 5px rgba(0, 0, 0, 0.1)' }}>Build a URL parameter</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <UrlParametersUpdate link={caroMediaFormik?.values} onClose={() => setUrlParamModalOpen(false)} onUrlChange={handleUrlParamChange} />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default CatalogueCaroAdUpdateForm;